import { useEffect, useState } from "react";
import { Check, Loader2, Tag, X, Eye, MessageSquare } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const norm = (s) => (s || "").trim().toUpperCase();

export const AssignmentDetail = ({ assignment, getAuthHeader, onClose }) => {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("items");
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    axios.get(`${API}/orders/assignments/${assignment.id}`, getAuthHeader())
      .then((r) => setData(r.data))
      .catch(() => toast.error("Couldn't load assignment"));
  }, [assignment.id, getAuthHeader]);

  const openLabel = async (labelId) => {
    try {
      const res = await fetch(`${API}/orders/labels/${labelId}/preview`, { headers: getAuthHeader().headers });
      if (!res.ok) throw new Error();
      setPreview(URL.createObjectURL(await res.blob()));
    } catch {
      toast.error("Couldn't load label preview");
    }
  };

  const items = data?.items || [];
  const labels = data?.labels || [];
  const pulled = items.filter((i) => i.pulled).length;
  const labelById = Object.fromEntries(labels.map((l) => [l.id, l]));
  const labelFor = (i) => labelById[i.pull_label_id] || labels.find((l) => norm(l.sku_tag) && norm(l.sku_tag) === norm(i.sku));
  const itemSkus = new Set(items.map((i) => norm(i.sku)).filter(Boolean));
  const linked = new Set(items.map((i) => i.pull_label_id).filter(Boolean));
  const labelHasItem = (l) => linked.has(l.id) || (norm(l.sku_tag) && itemSkus.has(norm(l.sku_tag)));

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-end sm:items-center justify-center" onClick={onClose} data-testid="assignment-detail">
      <div className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-4 border-b border-[#eee]">
          <div>
            <p className="text-sm font-semibold text-[#333]">{assignment.employee_name}</p>
            <p className="text-xs text-[#999]">
              {assignment.since}{assignment.until && assignment.until !== assignment.since && ` to ${assignment.until}`}
              {" · "}{assignment.status}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 text-[#999]" data-testid="assignment-detail-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex border-b border-[#eee]">
          {[["items", `Items (${items.length})`], ["labels", `Labels (${labels.length})`]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={`flex-1 py-2.5 text-xs font-semibold ${tab === k ? "text-[#8B5CF6] border-b-2 border-[#8B5CF6]" : "text-[#999]"}`} data-testid={`assignment-tab-${k}`}>{l}</button>
          ))}
        </div>

        <div className="overflow-y-auto p-3 space-y-1.5">
          {!data && <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-[#ccc] animate-spin" /></div>}

          {data && tab === "items" && (
            <>
              <p className="text-[11px] text-[#999] px-1 pb-1">{pulled} of {items.length} pulled</p>
              {items.map((i) => {
                const l = labelFor(i);
                return (
                  <div key={i.id} className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-[#fafafa] border border-[#eee]" data-testid={`detail-item-${i.id}`}>
                    <span className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${i.pulled ? "bg-[#10B981] text-white" : "border border-[#ddd]"}`}>{i.pulled && <Check className="w-3 h-3" />}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-bold font-mono ${i.sku ? "text-[#8B5CF6]" : "text-amber-600 text-[10px]"}`}>{i.sku || "No SKU"}</span>
                        {l && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FF6B35]/10 text-[#FF6B35] font-medium truncate">{l.display_name || l.filename}</span>}
                      </div>
                      <p className="text-xs text-[#666] truncate">{i.title || "Untitled"}</p>
                      {i.pull_note && <p className="text-[11px] text-[#8B5CF6] flex items-center gap-1"><MessageSquare className="w-3 h-3" />{i.pull_note}</p>}
                    </div>
                    {i.pull_image_url && <img src={i.pull_image_url} alt="" className="w-8 h-8 rounded object-cover border border-[#ddd]" />}
                  </div>
                );
              })}
            </>
          )}

          {data && tab === "labels" && labels.map((l) => (
            <div key={l.id} className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-[#fafafa] border border-[#eee]" data-testid={`detail-label-${l.id}`}>
              <Tag className="w-4 h-4 text-[#FF6B35] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold font-mono text-[#FF6B35]">{l.sku_tag || "No SKU"}</span>
                  {!labelHasItem(l) && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 border border-amber-200">no matching item</span>}
                </div>
                <p className="text-xs text-[#666] truncate">{l.display_name || l.filename}</p>
                {l.note && <p className="text-[11px] text-[#999] truncate">{l.note}</p>}
              </div>
              <button onClick={() => openLabel(l.id)} className="p-1.5 text-[#bbb] hover:text-[#8B5CF6]" data-testid={`detail-label-preview-${l.id}`}><Eye className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>

      {preview && (
        <div className="fixed inset-0 z-[90] bg-black/90 flex items-center justify-center p-4" onClick={(e) => { e.stopPropagation(); setPreview(null); }} data-testid="detail-label-lightbox">
          <img src={preview} alt="Label" className="max-w-full max-h-[85vh] object-contain rounded-lg bg-white" />
        </div>
      )}
    </div>
  );
};
